import { NotificationCenter } from '@components/Header/NotificationsCenter/NotificationCenter.models';
import {
  parseDownloadNotifications,
  parseUserNotifications,
} from '@components/Header/NotificationsCenter/NotificationsCenter.utils';
import { useAoisNotifications } from '@hooks/queries/notifications/useAoisNotifications';
import { useSystemNotifications } from '@hooks/queries/notifications/useSystemNotifications';
import { useUserNotifications } from '@hooks/queries/notifications/useUserNotifications';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useParams } from 'react-router';

const NotificationsCenterContext = createContext<{
  notifications: NotificationCenter.Notification[];
  tabFilter: 'all' | 'unread';
  setTabFilter: (filter: 'all' | 'unread') => void;
  popoverVisible: boolean;
  setPopoverVisible: (value: boolean) => void;
  unreadCount: number;
  markAllAsRead: () => void;
  markAsRead: (notification: NotificationCenter.Notification) => void;
}>({
  notifications: [],
  tabFilter: 'all',
  setTabFilter: () => {},
  popoverVisible: false,
  setPopoverVisible: () => {},
  unreadCount: 0,
  markAllAsRead: () => {},
  markAsRead: () => {},
});

const useNotificationsCenter = () => useContext(NotificationsCenterContext);

function NotificationsCenterProvider({ children }: Readonly<React.PropsWithChildren>) {
  const { propertyId } = useParams();
  const {
    readNotifications: readUserNotifications,
    data: userNotifications,
  } = useUserNotifications();
  const {
    data: aoisNotifications,
    readNotifications: readDownloadNotifications,
  } = useAoisNotifications({
    propertyId,
  });
  const {
    data: systemNotifications,
    readNotifications: readSystemNotifications,
  } = useSystemNotifications();

  const [popoverVisible, setPopoverVisible] = useState(false);

  const [tabFilter, setTabFilter] = useState<'all' | 'unread'>('all');

  const notifications = useMemo(() => {
    const parsedUserNotifications = parseUserNotifications(Object.values(userNotifications ?? []));
    const parsedSystemNotifications = parseUserNotifications(
      Object.values(systemNotifications ?? [])
    );
    const parsedDownloadNotifications = parseDownloadNotifications(
      Object.values(aoisNotifications ?? [])
    );

    const sortedNotifications = [
      ...parsedSystemNotifications,
      ...parsedUserNotifications,
      ...parsedDownloadNotifications,
    ].toSorted((a, b) => b.date - a.date);

    return sortedNotifications;
  }, [userNotifications, aoisNotifications, systemNotifications]);

  const filteredNotifications = useMemo(() => {
    if (tabFilter === 'unread') {
      return notifications.filter((notification) => notification.status === 'UNREAD');
    }

    return notifications;
  }, [notifications, tabFilter]);

  const unreadCount = useMemo(() => {
    return notifications.reduce((acc, cur) => {
      if (cur.status === 'UNREAD') {
        acc = acc + 1;
      }
      return acc;
    }, 0);
  }, [notifications]);

  const markAllAsRead = useCallback(() => {
    readDownloadNotifications(notifications.map((n) => n.id));
    readUserNotifications(notifications.map((n) => n.id));
    readSystemNotifications(notifications.map((n) => n.id));
  }, [readDownloadNotifications, readUserNotifications, readSystemNotifications, notifications]);

  const markAsRead = useCallback(
    (notification: NotificationCenter.Notification) => {
      const { service } = notification;

      if (service === 'image_download' || service === 'drone_upload') {
        readDownloadNotifications([notification.id]);
      }
      if (
        service === 'anomaly_notification' ||
        service === 'weekly_field_insights' ||
        service === 'ndvi_threshold_checker'
      ) {
        readUserNotifications([notification.id]);
      }
      if (service === 'system_notification') {
        readSystemNotifications([notification.id]);
      }
    },
    [readDownloadNotifications, readUserNotifications, readSystemNotifications]
  );

  const value = useMemo(
    () => ({
      notifications: filteredNotifications,
      tabFilter,
      setTabFilter,
      popoverVisible,
      setPopoverVisible,
      markAllAsRead,
      unreadCount,
      markAsRead,
    }),
    [
      filteredNotifications,
      tabFilter,
      setTabFilter,
      popoverVisible,
      setPopoverVisible,
      unreadCount,
      markAllAsRead,
      markAsRead,
    ]
  );

  return (
    <NotificationsCenterContext.Provider value={value}>
      {children}
    </NotificationsCenterContext.Provider>
  );
}

export { NotificationsCenterProvider, useNotificationsCenter };
